import { ChangeEvent, useState } from "react";
import { Form } from "react-bootstrap";
import Select from "react-select";
import TextField from "@components/FormsV2/TextField/TextField";
import TagsField from "@components/FormsV2/TagsField";
import GenericToggle from "@components/FormsV2/GenericToggle/GenericToggle";
import "./style.css";
import type { SurveyFormValues } from "../types";

export interface TeamOption {
  value: string;
  label: string;
}

export interface SurveyFormProps {
  id: string;
  initialValues: SurveyFormValues;
  teamOptions: TeamOption[];
  isSubmitting: boolean;
  onSubmit: (values: SurveyFormValues) => void;
}

export const SurveyForm = ({
  id,
  initialValues,
  teamOptions, // wired in Task 12
  isSubmitting,
  onSubmit,
}: SurveyFormProps): JSX.Element => {
  const [values, setValues] = useState<SurveyFormValues>(initialValues);

  const set = <K extends keyof SurveyFormValues>(
    key: K,
    v: SurveyFormValues[K]
  ) => setValues((prev) => ({ ...prev, [key]: v }));

  const handleSubmit = (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    if (isSubmitting) return;
    onSubmit(values);
  };

  return (
    <Form
      id={id}
      data-testid={id}
      className="survey-form"
      onSubmit={handleSubmit}
    >
      <div className="row">
        <div className="col-sm-6">
          <Form.Label htmlFor="questionnaire-name">Name</Form.Label>
          <TextField
            label={null}
            id="questionnaire-name"
            value={values.name}
            onChange={(e: ChangeEvent<HTMLInputElement>) =>
              set("name", e.target.value)
            }
            required
          />
        </div>
        <div className="col-sm-6">
          <Form.Label>Published?</Form.Label>
          <div className="survey-form__toggle">
            <GenericToggle
              key={`published-${initialValues.published}`}
              initialValueOfIsOn={values.published}
              onToggle={(on: boolean) => set("published", on)}
              onText="Published"
              offText="Draft"
              hasText
            />
          </div>
        </div>
      </div>

      <div className="row mt-3">
        <div className="col-sm-6">
          <Form.Label htmlFor="questionnaire-introduction">
            Introduction
          </Form.Label>
          <Form.Control
            as="textarea"
            id="questionnaire-introduction"
            rows={3}
            value={values.introduction}
            onChange={(e) => set("introduction", e.target.value)}
          />
          <small className="text-muted d-block">
            Shown at the top of the questionnaire.
          </small>
        </div>
        <div className="col-sm-6">
          <Form.Label htmlFor="questionnaire-thank-you">Thank You</Form.Label>
          <Form.Control
            as="textarea"
            id="questionnaire-thank-you"
            rows={3}
            value={values.thankYou}
            onChange={(e) => set("thankYou", e.target.value)}
          />
          <small className="text-muted d-block">Shown after completion.</small>
        </div>
      </div>

      <div className="row mt-3">
        <div className="col-sm-6">
          <Form.Label>Redirect after completion?</Form.Label>
          <div className="survey-form__toggle">
            <GenericToggle
              key={`redirect-${initialValues.redirect}`}
              initialValueOfIsOn={values.redirect}
              onToggle={(on: boolean) => set("redirect", on)}
              onText="Redirect"
              offText="No redirect"
              hasText
            />
          </div>
        </div>
        {values.redirect && (
          <div className="col-sm-6">
            <Form.Label htmlFor="questionnaire-completion-url">
              Completion URL
            </Form.Label>
            <TextField
              label={null}
              id="questionnaire-completion-url"
              value={values.completionUrl}
              onChange={(e: ChangeEvent<HTMLInputElement>) =>
                set("completionUrl", e.target.value)
              }
            />
          </div>
        )}
      </div>

      <div className="row mt-3">
        <div className="col-sm-6">
          <TagsField
            id="questionnaire-tags"
            value={values.tagList}
            onChange={(tags) => set("tagList", tags)}
          />
        </div>
      </div>

      <div className="row mt-3">
        <div className="col-sm-6">
          <Form.Label htmlFor="questionnaire-teams">Teams</Form.Label>
          <Select
            inputId="questionnaire-teams"
            isMulti
            options={teamOptions}
            value={teamOptions.filter((o) => values.teamIds.includes(o.value))}
            onChange={(opts) =>
              set(
                "teamIds",
                (opts as unknown as { value: string }[]).map((o) => o.value)
              )
            }
            placeholder="All teams"
          />
          <small className="text-muted d-block">
            Limit this questionnaire to specific teams (leave empty for all).
          </small>
        </div>
      </div>
    </Form>
  );
};

export default SurveyForm;
